import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_RECENT_POSTS } from '../../graphql/queries';

const Trending = () => {
    const publicUrl = process.env.PUBLIC_URL + '/';

    // Fetch recent posts for all sections
    const { data, loading, error } = useQuery(GET_RECENT_POSTS, {
        variables: { first: 13 }
    });

    const posts = data?.posts?.nodes || [];

    // Distribute posts across sections - 3 trending posts, 5 latest, 1 what's new
    const trendingPosts = posts.slice(0, 3);
    const latestPosts = posts.slice(3, 8);
    const whatsNewPosts = posts.slice(8, 9);

    // Helper function to format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    };

    // Helper function to get excerpt
    const getExcerpt = (excerpt, maxLength = 150) => {
        if (!excerpt) return '';
        const text = excerpt.replace(/<[^>]*>/g, '');
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // Helper function to get featured image URL
    const getFeaturedImage = (post, fallbackImage = 'post/5.png') => {
        return post?.featuredImage?.node?.sourceUrl || publicUrl + 'assets/img/' + fallbackImage;
    };

    // Helper function to get first category
    const getFirstCategory = (post) => {
        return post?.categories?.nodes?.[0] || { name: 'Uncategorized', slug: 'uncategorized' };
    };

    if (loading) {
        return (
            <div className="post-area pd-top-75 pd-bottom-50 go-top">
                <div className="container">
                    <div className="row">
                        <div className="col-12 text-center">
                            <p>Loading posts...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        console.error('Trending Error:', error);
        return null;
    }

    return (
        <div className="post-area pd-top-75 pd-bottom-50 go-top">
            <div className="container">
                <div className="row">
                    {/* Trending News */}
                    <div className="col-lg-3 col-md-6">
                        <div className="section-title">
                            <h6 className="title">Trending News</h6>
                        </div>
                        <div className="trending-post">
                            {trendingPosts.map((post, index) => (
                                <div className="single-post-wrap style-overlay" key={post.id}>
                                    <div className="thumb">
                                        <img
                                            src={getFeaturedImage(post, `post/${5 + index}.png`)}
                                            alt={post?.featuredImage?.node?.altText || post.title}
                                        />
                                    </div>
                                    <div className="details">
                                        <div className="post-meta-single">
                                            <p><i className="fa fa-clock-o" />{formatDate(post.date)}</p>
                                        </div>
                                        <h6 className="title">
                                            <Link
                                                to={`/blog/${post.slug}`}
                                                dangerouslySetInnerHTML={{ __html: post.title }}
                                            />
                                        </h6>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Latest News */}
                    <div className="col-lg-3 col-md-6">
                        <div className="section-title">
                            <h6 className="title">Latest News</h6>
                        </div>
                        <div className="post-list">
                            {latestPosts.map((post, index) => (
                                <div className="single-post-list-wrap" key={post.id}>
                                    <div className="media">
                                        <div className="media-left">
                                            <img
                                                src={getFeaturedImage(post, `post/list/${(index % 5) + 1}.png`)}
                                                alt={post?.featuredImage?.node?.altText || post.title}
                                            />
                                        </div>
                                        <div className="media-body">
                                            <div className="details">
                                                <div className="post-meta-single">
                                                    <ul>
                                                        <li><i className="fa fa-clock-o" />{formatDate(post.date)}</li>
                                                    </ul>
                                                </div>
                                                <h6 className="title">
                                                    <Link
                                                        to={`/blog/${post.slug}`}
                                                        dangerouslySetInnerHTML={{ __html: post.title }}
                                                    />
                                                </h6>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* What's New */}
                    <div className="col-lg-3 col-md-6">
                        <div className="section-title">
                            <h6 className="title">What's new</h6>
                        </div>
                        <div className="post-slider">
                            {whatsNewPosts.map((post, index) => {
                                const category = getFirstCategory(post);
                                return (
                                    <div className="single-post-wrap" key={post.id}>
                                        <div className="thumb">
                                            <img
                                                src={getFeaturedImage(post, 'post/8.png')}
                                                alt={post?.featuredImage?.node?.altText || post.title}
                                            />
                                        </div>
                                        <div className="details">
                                            <div className="post-meta-single mb-4 pt-1">
                                                <ul>
                                                    <li>
                                                        <Link className="tag-base tag-blue" to={`/cat-page`}>
                                                            {category.name}
                                                        </Link>
                                                    </li>
                                                    <li><i className="fa fa-clock-o" />{formatDate(post.date)}</li>
                                                </ul>
                                            </div>
                                            <h6 className="title">
                                                <Link
                                                    to={`/blog/${post.slug}`}
                                                    dangerouslySetInnerHTML={{ __html: post.title }}
                                                />
                                            </h6>
                                            <p>{getExcerpt(post.excerpt)}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Join With Us */}
                    <div className="col-lg-3 col-md-6">
                        <div className="section-title">
                            <h6 className="title">Join With Us</h6>
                        </div>
                        <div className="social-area-list mb-4">
                            <ul>
                                <li><a className="facebook" href="#"><i className="fa fa-facebook social-icon" /><span>12,300</span><span>Like</span> <i className="fa fa-plus" /></a></li>
                                <li><a className="twitter" href="#"><i className="fa fa-twitter social-icon" /><span>12,600</span><span>Followers</span> <i className="fa fa-plus" /></a></li>
                                <li><a className="youtube" href="#"><i className="fa fa-youtube-play social-icon" /><span>1,300</span><span>Subscribers</span> <i className="fa fa-plus" /></a></li>
                                <li><a className="instagram" href="#"><i className="fa fa-instagram social-icon" /><span>52,400</span><span>Followers</span> <i className="fa fa-plus" /></a></li>
                                <li><a className="google-plus" href="#"><i className="fa fa-google social-icon" /><span>19,101</span><span>Subscribers</span> <i className="fa fa-plus" /></a></li>
                            </ul>
                        </div>
                        <div className="add-area">
                            <Link to="/blog-details"><img className="w-100" src={publicUrl + "assets/img/add/6.png"} alt="img" /></Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Trending;
